import React, { useState } from 'react'
import { Menu, Select, Tooltip, Card, type MenuProps } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { renderAppIcon } from '../../../ui_configs/iconMap.tsx'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setAreaFromUser } from '../../../store/slices/menuAreaSlice.ts'
import type { FooterSelectItem, MenuSection } from '../../../ui_configs/left_menu.ts'

export interface LeftMenuProps {
  sections?: MenuSection[]
  footerSelectItems?: FooterSelectItem[]
  currentPath?: string
  footerSelectionKey?: string | null
  selectedMenuKeys?: string[] | null
  className?: string
  isVisible?: boolean
  isCollapsed?: boolean
}

type MenuItemType = NonNullable<MenuProps['items']>[number]

export function LeftMenu({
  sections = [],
  footerSelectItems = [],
  currentPath = '',
  footerSelectionKey = null,
  selectedMenuKeys: selectedMenuKeysProp = null,
  className = '',
  isVisible = true,
  isCollapsed = false
}: LeftMenuProps) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [openKeys, setOpenKeys] = useState(sections.filter((s) => s.defaultExpanded).map((s) => s.key))
  const [isFooterSelectOpen, setIsFooterSelectOpen] = useState(false)

  if (!isVisible) return null

  const handleMenuClick = ({ key }: { key: string }) => {
    // Find the menu item by key across all sections
    for (const section of sections) {
      const item = section.items.find((item) => item.key === key)
      if (item) {
        // Navigate to the item's href directly - this will trigger the tab manager
        // to create/activate the appropriate tab based on the route
        navigate(item.href)
        break
      }
    }
  }

  const handleSelectChange = (value: string) => {
    const selectedItem = footerSelectItems.find((item) => item.key === value)
    if (selectedItem) {
      // Record the explicit area choice and STOP. Switching area repaints this
      // pane only - it opens no tab and disturbs no open one (owner 2026-09-02).
      // The previous navigate() to the area's default item opened a tab nobody
      // asked for, and pushed whatever you were reading out of view.
      dispatch(setAreaFromUser(selectedItem.key))
    }
  }

  const getSelectedKeys = () => {
    if (selectedMenuKeysProp != null && selectedMenuKeysProp.length > 0) return selectedMenuKeysProp
    for (const section of sections) {
      const exact = section.items.find((item) => item.href === currentPath)
      if (exact) return [exact.key]
      const prefix = section.items
        .filter((item) => currentPath.startsWith(`${item.href}/`))
        .sort((a, b) => b.href.length - a.href.length)[0]
      if (prefix) return [prefix.key]
    }
    return []
  }

  // Convert sections to menu items
  const menuItems = sections.flatMap((section): MenuItemType | MenuItemType[] => {
    const items = section.items.map((item) => ({
      key: item.key,
      icon: renderAppIcon(item.icon, { color: '#268ED2' }),
      label: item.label
    }))

    // When collapsed, omit group titles to prevent heading overlap
    if (isCollapsed) {
      return items
    }

    // Expanded behaviour
    if (section.isExpandable) {
      return {
        key: section.key,
        label: (
          <span style={{ color: '#268ED2', fontWeight: '600', fontSize: '12px', letterSpacing: '0.5px' }}>
            {section.title}
          </span>
        ),
        children: items,
        icon: <FontAwesomeIcon icon={faChevronDown} style={{ color: '#268ED2' }} />
      }
    }

    return {
      type: 'group',
      label: (
        <span style={{ color: '#268ED2', fontWeight: '600', fontSize: '12px', letterSpacing: '0.5px' }}>
          {section.title}
        </span>
      ),
      children: items
    }
  })

  // Straight from the single resolved area - deliberately NOT re-derived from
  // the path here. The old href fallback would flip this label whenever the
  // current path matched another area's default item, which is exactly the
  // menu/footer disagreement this change removes.
  const currentFooterSelection = footerSelectionKey

  return (
    <div
      className={`${isCollapsed ? 'w-12' : 'w-60'} h-full overflow-hidden transition-all duration-300 ease-in-out ${className}`}
      style={{
        background: '#F0F7FF',
        position: 'relative',
        zIndex: 60,
        borderRight: '1px solid #C7E0FF'
      }}
    >
      {/* Custom styles for menu and tooltips */}
      <style>
        {`
          .custom-menu .ant-menu-item:hover {
            background-color: #3B82F6 !important;
            color: #FFFFFF !important;
          }
          .custom-menu .ant-menu-item:hover .ant-menu-item-icon {
            color: #FFFFFF !important;
          }
          .custom-menu .ant-menu-item:hover span {
            color: #FFFFFF !important;
          }
          .custom-menu .ant-menu-submenu-title:hover {
            background-color: #3B82F6 !important;
            color: #FFFFFF !important;
          }
          .custom-menu .ant-menu-submenu-title:hover .ant-menu-submenu-arrow {
            color: #FFFFFF !important;
          }
          .custom-menu .ant-menu-submenu-title:hover .anticon {
            color: #FFFFFF !important;
          }
          .hover\\:bg-blue-500:hover svg {
            color: #FFFFFF !important;
          }
          .custom-menu .ant-menu-item,
          .custom-menu .ant-menu-submenu-title,
          .custom-menu .ant-menu-item-group-title {
            transition: all 0.3s ease-in-out !important;
          }
          .custom-menu .ant-menu-item-icon,
          .custom-menu .anticon {
            transition: all 0.3s ease-in-out !important;
          }
          ${
            isCollapsed
              ? `
            .ant-tooltip-inner {
              background: #FFFFFF !important;
              color: #268ED2 !important;
              font-weight: 500 !important;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
              border: 1px solid #E5E7EB !important;
              border-radius: 8px !important;
            }
            .ant-tooltip-arrow::before {
              background: #FFFFFF !important;
              border: 1px solid #E5E7EB !important;
            }
          `
              : ''
          }
        `}
      </style>
      <Card
        className='h-full rounded-none shadow-none border-0 flex flex-col'
        style={{ background: '#F0F7FF' }}
        styles={{
          body: { padding: 0, display: 'flex', flexDirection: 'column', background: '#F0F7FF', height: '100%' }
        }}
      >
        {/* Main Menu Content */}
        <div className='flex-1 overflow-auto pt-4'>
          <Menu
            mode='inline'
            inlineCollapsed={isCollapsed}
            selectedKeys={getSelectedKeys()}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            onClick={handleMenuClick}
            items={menuItems}
            style={{
              background: '#F0F7FF',
              border: 'none',
              width: isCollapsed ? '100%' : 'auto',
              fontSize: '15px'
            }}
            theme='light'
            className='custom-menu'
          />
        </div>

        {/* Footer Select */}
        {footerSelectItems.length > 0 && (
          <div
            className={`mt-auto ${isCollapsed ? 'p-2' : 'p-4'} transition-all duration-300 ease-in-out`}
            style={{
              background: '#E1F0FF',
              borderTop: '1px solid #C7E0FF',
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.05)'
            }}
          >
            {isCollapsed ? (
              // Collapsed: Show all icons stacked vertically
              <div className='flex flex-col gap-2 items-center'>
                {footerSelectItems.map((item) => (
                  <Tooltip
                    key={item.key}
                    title={item.label}
                    placement='right'
                    overlayStyle={{
                      background: '#FFFFFF',
                      color: '#268ED2',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px'
                    }}
                    overlayInnerStyle={{
                      background: '#FFFFFF',
                      color: '#268ED2',
                      fontWeight: '500'
                    }}
                  >
                    <button
                      className={`w-8 h-8 flex items-center justify-center transition-all duration-300 ease-in-out cursor-pointer hover:scale-105 active:scale-95 ${
                        item.key === currentFooterSelection
                          ? 'bg-blue-100 rounded-md border-r-2 border-blue-600'
                          : 'hover:bg-blue-500 hover:text-white rounded-lg'
                      }`}
                      onClick={() => handleSelectChange(item.key)}
                    >
                      {renderAppIcon(item.icon, {
                        color: item.key === currentFooterSelection ? '#1677ff' : '#6B7280',
                        fontSize: '16px',
                        transition: 'all 0.3s ease-in-out'
                      })}
                    </button>
                  </Tooltip>
                ))}
              </div>
            ) : (
              // Expanded: Show full select dropdown
              <Select
                value={currentFooterSelection}
                onChange={handleSelectChange}
                className='w-full'
                variant='borderless'
                size='middle'
                style={{
                  background: '#FFFFFF',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #C7E0FF',
                  fontSize: '14px'
                }}
                suffixIcon={
                  <FontAwesomeIcon
                    icon={isFooterSelectOpen ? faChevronDown : faChevronUp}
                    style={{ color: '#268ED2', fontSize: '14px' }}
                  />
                }
                onOpenChange={setIsFooterSelectOpen}
                styles={{
                  popup: {
                    root: {
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #E5E7EB',
                      padding: '4px 0',
                      minWidth: '220px',
                      left: 0,
                      fontSize: '14px'
                    }
                  }
                }}
                popupRender={(menu) => (
                  <div>
                    <style>
                      {`
                        .ant-select-selector {
                          padding: 6px 12px !important;
                        }
                        .ant-select-selection-item {
                          padding: 0 !important;
                          display: flex !important;
                          align-items: center !important;
                          width: 100% !important;
                        }
                        .ant-select-item-option-selected {
                          background-color: #DBEAFE !important;
                          border-right: 2px solid #2563EB !important;
                          color: #1677ff !important;
                        }
                        .ant-select-item-option-selected .ant-select-item-option-content {
                          color: #1677ff !important;
                        }
                        .ant-select-item-option:hover {
                          background-color: #3B82F6 !important;
                          color: #FFFFFF !important;
                        }
                        .ant-select-item-option:hover .ant-select-item-option-content {
                          color: #FFFFFF !important;
                        }
                        .ant-select-item-option:hover .ant-select-item-option-content span {
                          color: #FFFFFF !important;
                        }
                        .ant-select-item-option:hover .ant-select-item-option-content .anticon {
                          color: #FFFFFF !important;
                        }
                        .ant-select-item-option:hover span {
                          color: #FFFFFF !important;
                        }
                        .ant-select-item-option:hover .anticon {
                          color: #FFFFFF !important;
                        }
                        .ant-select-item-option:hover svg {
                          color: #FFFFFF !important;
                        }
                      `}
                    </style>
                    {menu}
                  </div>
                )}
                placement='topLeft'
                optionLabelProp='customLabel'
              >
                {footerSelectItems.map((item) => (
                  <Select.Option
                    key={item.key}
                    value={item.key}
                    customLabel={
                      <div className='flex items-center gap-2' style={{ padding: '0' }}>
                        {renderAppIcon(item.icon, { color: '#268ED2', fontSize: '14px', minWidth: '14px' })}
                        <span
                          style={{
                            fontSize: '14px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {item.label}
                        </span>
                      </div>
                    }
                  >
                    <div className='flex items-center gap-2 py-2 px-2' style={{ minWidth: '200px' }}>
                      {renderAppIcon(item.icon, {
                        color: '#268ED2',
                        fontSize: '14px',
                        transition: 'color 0.3s ease-in-out'
                      })}
                      <span
                        className='text-gray-800 font-medium whitespace-nowrap'
                        style={{
                          fontSize: '14px',
                          transition: 'color 0.3s ease-in-out'
                        }}
                      >
                        {item.label}
                      </span>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            )}
          </div>
        )}
      </Card>
    </div>
  )
}
